import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import {
  useStaffProfile,
  useUpdateStaffProfile,
  useUploadStaffProfileImage,
} from "@/hooks/use-staff";
import { Camera, Loader2, ArrowLeft, Save, RotateCcw, User } from "lucide-react";

type Form = {
  name: string;
  phone: string;
  jobTitle: string;
  department: string;
  bio: string;
};

const EMPTY_FORM: Form = { name: "", phone: "", jobTitle: "", department: "", bio: "" };

function initials(nameOrEmail: string): string {
  const parts = nameOrEmail.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (nameOrEmail[0] || "?").toUpperCase();
}

export default function StaffProfilePage({ staffId }: { staffId: number }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: profile, isLoading, error } = useStaffProfile(staffId);
  const updateProfile = useUpdateStaffProfile();
  const uploadImage = useUploadStaffProfileImage();

  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Seed form once profile loads
  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.full_name || [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "",
        phone: profile.phone || "",
        jobTitle: profile.job_title || "",
        department: profile.department || "",
        bio: profile.bio || "",
      });
    }
  }, [profile]);

  const resetForm = () => {
    if (!profile) return;
    setForm({
      name: profile.full_name || [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "",
      phone: profile.phone || "",
      jobTitle: profile.job_title || "",
      department: profile.department || "",
      bio: profile.bio || "",
    });
    setImagePreview(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    if (form.bio.length > 500) {
      toast({ title: "Bio must be 500 characters or fewer", variant: "destructive" });
      return;
    }
    try {
      await updateProfile.mutateAsync({
        staffId,
        data: {
          name: form.name.trim(),
          phone: form.phone || null,
          jobTitle: form.jobTitle || null,
          department: form.department || null,
          bio: form.bio || null,
        },
      });
      toast({ title: "Profile updated" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Invalid file type", description: "Use JPG, PNG, or WEBP", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max size is 5MB", variant: "destructive" });
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    try {
      await uploadImage.mutateAsync({ staffId, file });
      toast({ title: "Image uploaded" });
    } catch (err: any) {
      setImagePreview(null);
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (error || !profile) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12 text-center">
          <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Profile unavailable</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {(error as Error)?.message || "Unable to load staff profile."}
          </p>
          <Button variant="outline" onClick={() => navigate("/staff")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Staff
          </Button>
        </div>
      </Layout>
    );
  }

  const displayImage =
    imagePreview || profile.profile_image_url || profile.profile_picture || undefined;
  const displayName = form.name || profile.full_name || profile.email;
  const isSaving = updateProfile.isPending;
  const isUploading = uploadImage.isPending;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 pb-12">
        {/* Back link */}
        <Button variant="ghost" size="sm" onClick={() => navigate("/staff")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Staff
        </Button>

        {/* CARD 1 — Profile Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              {/* Avatar with upload overlay */}
              <div className="relative group">
                <button
                  type="button"
                  onClick={handleAvatarClick}
                  disabled={isUploading}
                  className="relative block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full"
                  aria-label="Upload profile image"
                >
                  <Avatar className="w-28 h-28 border-4 border-background shadow-md">
                    {displayImage ? (
                      <AvatarImage src={displayImage} alt={displayName} />
                    ) : null}
                    <AvatarFallback className="text-2xl font-semibold bg-primary/10 text-primary">
                      {initials(displayName)}
                    </AvatarFallback>
                  </Avatar>

                  {/* Camera overlay */}
                  <span className="absolute bottom-0 right-0 bg-primary text-primary-foreground rounded-full p-2 shadow-md border-2 border-background group-hover:scale-110 transition-transform">
                    {isUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                  </span>

                  {isUploading && (
                    <span className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-white" />
                    </span>
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              {/* Name + badges */}
              <div className="flex-1 text-center sm:text-left space-y-2">
                <h1 className="text-2xl font-bold">{displayName}</h1>
                <p className="text-sm text-muted-foreground">{profile.email}</p>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                  <Badge variant={profile.role === "admin" ? "default" : "secondary"} className="capitalize">
                    {profile.role}
                  </Badge>
                  <Badge variant={profile.is_active ? "default" : "destructive"}>
                    {profile.is_active ? "Active" : "Inactive"}
                  </Badge>
                  {profile.invite_accepted === false && (
                    <Badge variant="outline">Pending Invite</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CARD 2 — Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name <span className="text-destructive">*</span></Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" value={profile.email} readOnly disabled className="bg-muted" />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 555 123 4567"
                />
              </div>
              <div>
                <Label htmlFor="jobTitle">Job Title</Label>
                <Input
                  id="jobTitle"
                  value={form.jobTitle}
                  onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
                  placeholder="e.g. Senior Accountant"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  placeholder="e.g. Finance"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CARD 3 — About */}
        <Card>
          <CardHeader>
            <CardTitle>About</CardTitle>
          </CardHeader>
          <CardContent>
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 500) })}
              placeholder="A short bio..."
              rows={5}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">
              {form.bio.length}/500
            </p>
          </CardContent>
        </Card>

        {/* CARD 4 — Actions */}
        <Card>
          <CardContent className="flex items-center justify-end gap-2 py-4">
            <Button variant="outline" onClick={resetForm} disabled={isSaving}>
              <RotateCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !form.name.trim()}>
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
